import { Routes } from '@angular/router';
import { authGuard } from './auth.guard';
import { rotationGuard, rotationPendingGuard } from './rotation.guard';
import { tierGuard } from './tier.guard';

export const routes: Routes = [

  // ---------------------------
  // Public routes (no guard)
  // ---------------------------
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },

  {
    path: 'login',
    loadComponent: () =>
      import('./views/pages/login/login.component').then(m => m.LoginComponent),
    data: { title: 'Login Page' }
  },
  {
    // The branded entry page (organizations phase 4): the same login,
    // wearing the org's public face fetched by slug. `pathMatch: 'full'`
    // keeps it to the bare /o/<slug> — everything under that base is the
    // org's own console, below in the protected group.
    path: 'o/:slug',
    pathMatch: 'full',
    loadComponent: () =>
      import('./views/pages/login/login.component').then(m => m.LoginComponent),
    data: { title: 'Sign in' }
  },
  {
    path: '404',
    loadComponent: () =>
      import('./views/pages/page404/page404.component').then(m => m.Page404Component),
    data: { title: 'Page 404' }
  },

  {
    path: '500',
    loadComponent: () =>
      import('./views/pages/page500/page500.component').then(m => m.Page500Component),
    data: { title: 'Page 500' }
  },

  {
    // Full-page, outside the CMS chrome: a signed-in staff member whose
    // password is under the floor rotates it here before any other page.
    // rotationPendingGuard bounces anyone without a pending rotation to the
    // dashboard, so it cannot be lingered on.
    path: 'first-sign-in',
    canActivate: [authGuard, rotationPendingGuard],
    loadComponent: () =>
      import('./views/pages/first-sign-in/first-sign-in.component')
        .then(m => m.FirstSignInComponent),
    data: { title: 'Update your password' }
  },

  // ---------------------------
  // Protected routes (inside layout)
  // ---------------------------
  {
    path: '',
    canActivate: [authGuard, rotationGuard],
    canActivateChild: [tierGuard],
    loadComponent: () =>
      import('./layout').then((m) => m.DefaultLayoutComponent),
    children: [

      {
        path: 'dashboard',
        canActivate: [authGuard],
        loadChildren: () =>
          import('./views/dashboard/routes').then((m) => m.routes)
      },







      {
        path: 'profile',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/profile/profile.component').then(m => m.ProfileComponent),
        data: { title: 'My profile' }
      },
      {
        path: 'users',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/user-list/user-list.component').then(m => m.UserListComponent),
      },
      {
        path: 'users/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/user-detail/user-detail.component').then(m => m.UserDetailComponent),
      },
      {
        path: 'guests',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/guests/guests.component').then(m => m.GuestsComponent),
        data: { title: 'Guests' }
      },
      {
        path: 'tickets',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/ticket-list/ticket-list.component').then(m => m.TicketListComponent),
      },
      {
        path: 'messages/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./views/pages/message/message.component').then(m => m.MessageComponent),
      },
      {
        path: 'register',
        loadComponent: () =>
          import('./views/pages/register/register.component').then(m => m.RegisterComponent),
        data: { title: 'Register Page' }
      },
      {
        path: 'add-ticket',
        canActivate: [authGuard],
        loadComponent: () => import('./views/pages/ticket/ticket.component').then(m => m.TicketComponent),
        data: {
          title: 'Add Ticket'
        }
      },
        {
        path: 'posture',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/posture-list/posture-list.component').then(m => m.PostureListComponent),
      },
      {
        path: 'posture/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/posture-report/posture-report.component').then(m => m.PostureReportComponent),
      },
      {
        path: 'article',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./views/pages/article/article.component').then(m => m.ArticleComponent),
        data: { title: 'Article' }
      },
      {
        path: 'article/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./views/pages/article/article.component').then(m => m.ArticleComponent),
        data: { title: 'Article' }
      },
      {
        path: 'article-categories',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/article-category-list/article-category-list.component').then(m => m.ArticleCategoryListComponent),
      },
      {
        path: 'article-category',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./views/pages/article-category/article-category.component').then(m => m.ArticleCategoryComponent),
      },
      {
        path: 'article-category/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./views/pages/article-category/article-category.component').then(m => m.ArticleCategoryComponent),
      },
      {
        path: 'articles',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/article-list/article-list.component').then(m => m.ArticleListComponent),
      },
      {
        path: 'notification-templates',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/notification-template-list/notification-template-list.component')
            .then(m => m.NotificationTemplateListComponent),
      },
      {
        path: 'notification-template',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/notification-template-edit/notification-template-edit.component')
            .then(m => m.NotificationTemplateEditComponent),
      },
      {
        path: 'notification-template/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/notification-template-edit/notification-template-edit.component')
            .then(m => m.NotificationTemplateEditComponent),
      },
      {
        path: 'email-templates',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/email-templates/email-templates.component')
            .then(m => m.EmailTemplatesComponent),
        data: { title: 'Emails' }
      },
      {
        path: 'broadcast',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/broadcast/broadcast.component')
            .then(m => m.BroadcastComponent),
        data: { title: 'Broadcast' }
      },
      {
        path: 'settings',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/settings/settings.component')
            .then(m => m.SettingsComponent),
        data: { title: 'Settings' }
      },
      {
        // Lifted off the Settings page 2026-09-16: it changes balances, not
        // configuration, and it carries its own preview-and-confirm flow.
        path: 'bulk-credits',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/bulk-credits/bulk-credits.component')
            .then(m => m.BulkCreditsComponent),
        data: { title: 'Bulk credits' }
      },
      {
        path: 'learn',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/learn-content/learn-content.component')
            .then(m => m.LearnContentComponent),
      },
      {
        path: 'notify-relese',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/notify-relese-list/notify-relese-list.component').then(m => m.NotifyReleseListComponent),
      },
      {
        path: 'audit',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/audit-list/audit-list.component').then(m => m.AuditListComponent),
        data: { title: 'Audit log' }
      },
      {
        path: 'staff',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/staff/staff.component').then(m => m.StaffComponent),
        data: { title: 'Staff' }
      },
      {
        path: 'deletion-requests',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/deletion-requests/deletion-requests.component').then(m => m.DeletionRequestsComponent),
        data: { title: 'Deletion requests' }
      },
      {
        path: 'grant-requests',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/grant-requests/grant-requests.component').then(m => m.GrantRequestsComponent),
        data: { title: 'Grant requests' }
      },
      {
        path: 'canned-replies',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/canned-replies/canned-replies.component').then(m => m.CannedRepliesComponent),
        data: { title: 'Saved replies' }
      },
      {
        path: 'organizations',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/organizations/organizations.component').then(m => m.OrganizationsComponent),
        data: { title: 'Organizations' }
      },
      {
        path: 'organizations/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/organization-detail/organization-detail.component')
            .then(m => m.OrganizationDetailComponent),
        data: { title: 'Organization' }
      },
      {
        path: 'org-activity',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/org-activity/org-activity.component')
            .then(m => m.OrgActivityComponent),
        data: { title: 'Org activity' }
      },
      // ---- The org console, on the org's own base ----
      // Every org surface is /o/<slug>/<page>: an organization is never a
      // guest on a muufree URL. The slug is display only — the backend
      // scopes each request to the caller's own org — so tierGuard treats
      // a wrong or missing slug as a redirect, not as access.
      {
        path: 'o/:slug/dashboard',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/org/org-dashboard.component')
            .then(m => m.OrgDashboardComponent),
        data: { title: 'Dashboard' }
      },
      {
        path: 'o/:slug/people',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/org/org-people.component')
            .then(m => m.OrgPeopleComponent),
        data: { title: 'People' }
      },
      {
        // The shared posture list: the backend scopes it to the org.
        path: 'o/:slug/reports',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/posture-list/posture-list.component')
            .then(m => m.PostureListComponent),
        data: { title: 'Posture reports' }
      },
      {
        // Opening a report must not drop the org onto a muufree URL.
        path: 'o/:slug/reports/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/posture-report/posture-report.component')
            .then(m => m.PostureReportComponent),
        data: { title: 'Posture report' }
      },
      {
        path: 'o/:slug/pool',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/org/org-pool.component')
            .then(m => m.OrgPoolComponent),
        data: { title: 'Scan pool' }
      },
      {
        path: 'o/:slug/activity',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/org/org-own-activity.component')
            .then(m => m.OrgOwnActivityComponent),
        data: { title: 'Activity' }
      },
      {
        path: 'o/:slug/support',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/org/org-support.component')
            .then(m => m.OrgSupportComponent),
        data: { title: 'Support' }
      },
      {
        path: 'o/:slug/settings',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/org/org-settings.component')
            .then(m => m.OrgSettingsComponent),
        data: { title: 'Settings' }
      },
      {
        // The retired base. Old links and bookmarks still carry it;
        // tierGuard rewrites them onto /o/<slug>/<same page>.
        path: 'org/:page',
        canActivate: [authGuard],
        children: []
      },
    ]
  },

  {
    path: '**',
    redirectTo: '404'
  }
];
